import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { createPortal } from "react-dom";

import { MainHeaderContext } from "../../../components/main-header/main-header-state";

import List, { ListOrderStates } from "../../../components/list/list";

import "./devices.css";
import Button from "../../../components/button/button";
import isElectron from "../../server/is-electron";
import { useToasts } from "react-toast-notifications";
import { BackendContext, backendStatus } from "../../../backend/backend";
import DeviceDetails from "./device-details";
import DownloadApps from "../../components/download-apps";
import DiagramRemotize from "../../components/diagram";
import DeviceTour from "./device-tour";
import { UIEventsContext } from "../../uievents-context";
import Pager from "../../../components/pager/pager";
import DeviceAsssociator from "./device-associator";
import MultiFieldSearch from "./multifield-search";
import SearchIcon from "../../../components/icons/search-icon";
import { UserContext } from "../../user-context";
import Tabs from "../../../components/tabs/tabs";
import deviceFactory from "../../apis/device-factory";
import Loading from "../../../components/loading/loading";
import InfoPopup from "../../../components/info-popup/info-popup";
import DeviceFactory from "../../apis/device-factory";
import HelpIcon from "../../../components/icons/help-icon";
import TooltipText from "../../../components/tooltip-text/tooltip-text";
import Modal, { ModalTypes } from "../../../components/modal/modal";
import CircledArrow from "../../../components/icons/circled-arrow";
import {
  isMeshlistedEnabled,
  fetchMeshRoles,
  meshRole,
  meshRoleLabels,
} from "../../apis/meshlisted";

export const profileStatus = {
  NO_PROFILE: 0,
  ASSOCIATING: 1,
  CONFIRMED: 2,
  REAPLY: 4,
};

export const deviceStatus = {
  WAITING: 0,
  OFFLINE: 1,
  ONLINE: 2,
  CONFIRMING: 3,
  UPDATING: 4,
  ERROR: 5,
};

export const assocStatus = {
  NOT_ASSOCIATED: 0,
  ASSOCIATING: 1,
  ASSOCIATED: 2,
};

const FIND_TIMEOUT = 30;

export function profileIsApplying(device) {
  if (DeviceFactory.isTR069(device.model)) return false;

  return (
    device.profile.status === profileStatus.ASSOCIATING ||
    device.profile.status === profileStatus.REAPLY
  );
}

export function canBeManaged(device) {
  let { assoc_status } = device;

  return (
    assoc_status === assocStatus.ASSOCIATED &&
    deviceFactory.haveManagementAPI(device)
  );
}

function getCategoryFromPath(history) {
  let pathname = history.location.pathname;

  if (pathname.charAt(pathname.length - 1) === "/") {
    history.push("0");
  }

  return pathname.charAt(pathname.length - 1);
}

function buildDevicesFilterQuery(searchQuery, status) {
  const params = new URLSearchParams(searchQuery || "");
  params.delete("status");
  if (status) params.set("status", status);
  return params.toString();
}

async function fetchDevices(
  history,
  backend,
  setDevices,
  setLoading,
  pagerInfo,
  setPagerInfo,
  filters,
  sort
) {
  const { pageSize, pageNumber } = pagerInfo;

  let category = getCategoryFromPath(history);

  let result = await backend.retrieveFresh(
    `devices?category=${category}&pages=y&pagesize=${pageSize}&pagenumber=${pageNumber}${
      filters ? "&" + filters : ""
    }${sort ? "&" + sort : ""}`
  );

  if (result.status !== backendStatus.SUCCESS) {
    return;
  }

  pagerInfo.totalElements = result.headers.get("x-devices-total");
  pagerInfo.hasGateways = result.headers.get("x-has-gateway") === "true";

  setPagerInfo({ ...pagerInfo });

  if (result.status !== backendStatus.SUCCESS) return;

  setDevices(() => {
    setLoading(false);
    return result.content;
  });
}

async function fetchProfiles(backend, setProfiles, setLoading) {
  let result = await backend.retrieveFresh("profiles?idsonly=y");

  if (result.status !== backendStatus.SUCCESS) return;

  setProfiles(() => {
    setLoading(false);
    return result.content;
  });
}

function handleProfileName(device) {
  if (!device.profile) {
    device.profile = {
      name: "Sem perfil",
      status: 0,
    };
  }
}

function deviceNeedsUpdate(device, newDevice) {
  return device.clientid !== newDevice.clientid;
}
function deviceProfileNeedsUpdate(device, newDevice) {
  return device.profile.profileid !== newDevice.profile.profileid;
}

const getDateString = (value) => {
  let date = new Date(value);

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false, // 24h
  });
};

const DevicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [buttonLoading, setbuttonLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [firmwareUpdatePreviews, setFirmwareUpdatePreviews] = useState({});
  const [firmwareUpdate, setFirmwareUpdate] = useState(null);
  const [firmwareUpdateSaving, setFirmwareUpdateSaving] = useState(false);
  const [username, setUsername] = useState("");
  const [searched, setSearched] = useState(false);
  const [filterQueryString, setFilterQueryString] = useState("");
  const [searchFilterQueryString, setSearchFilterQueryString] = useState("");
  const [deviceStatusFilter, setDeviceStatusFilter] = useState("");
  const [sortQueryString, setSortQueryString] = useState(null);
  const { addToast } = useToasts();

  const queryRef = useRef(filterQueryString);
  const sortRef = useRef(sortQueryString);

  const [pagerInfo, setPagerInfo] = useState({
    pageSize: 20,
    pageNumber: 1,
    totalElements: 0,
    hasGateways: false,
  });

  const header = useContext(MainHeaderContext);
  const backend = useContext(BackendContext);
  const uievents = useContext(UIEventsContext);

  const history = useHistory();
  const [user] = useContext(UserContext);

  // EasyMesh role column, served by meshlisted only for the ISPs it lists in
  // GET /config. Routers tab only (category 0); no history side effects while
  // rendering.
  const [meshlistedEnabled, setMeshlistedEnabled] = useState(false);
  const [meshRoles, setMeshRoles] = useState({});

  // Popover listing the secondaries of a primary. The list clips its cells
  // (overflow: hidden), so the popover is rendered through a portal on
  // document.body, positioned from the badge's viewport rectangle.
  const [meshPopover, setMeshPopover] = useState(null);
  const meshPopoverTimer = useRef(null);

  const openMeshPopover = (event, deviceid) => {
    clearTimeout(meshPopoverTimer.current);
    const rect = event.currentTarget.getBoundingClientRect();
    setMeshPopover({ deviceid, top: rect.bottom + 6, left: rect.left });
  };

  // Delayed so the pointer can cross the gap between the badge and the popover.
  const closeMeshPopover = () => {
    clearTimeout(meshPopoverTimer.current);
    meshPopoverTimer.current = setTimeout(() => setMeshPopover(null), 150);
  };

  const keepMeshPopover = () => clearTimeout(meshPopoverTimer.current);

  useEffect(() => {
    if (!meshPopover) return undefined;

    // Scrolling moves the badge away from the fixed popover: just close it.
    const close = () => setMeshPopover(null);
    window.addEventListener("scroll", close, true);

    return () => window.removeEventListener("scroll", close, true);
  }, [meshPopover]);

  useEffect(() => () => clearTimeout(meshPopoverTimer.current), []);

  useEffect(() => {
    let cancelled = false;

    isMeshlistedEnabled(user).then((enabled) => {
      if (!cancelled) setMeshlistedEnabled(enabled);
    });

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line
  }, [user?.ispid]);
  const showMeshColumn = () => {
    const pathname = history.location.pathname;

    return meshlistedEnabled && pathname.charAt(pathname.length - 1) === "0";
  };

  useEffect(() => {
    if (!showMeshColumn()) {
      setMeshRoles({});
      return;
    }

    let cancelled = false;

    fetchMeshRoles(
      backend,
      devices.map((device) => device.deviceid)
    ).then((roles) => {
      if (!cancelled) setMeshRoles(roles);
    });

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line
  }, [devices, meshlistedEnabled, history.location.pathname]);

  useEffect(() => {
    queryRef.current = filterQueryString;
    sortRef.current = sortQueryString;
  }, [filterQueryString, sortQueryString]);

  useEffect(() => {
    let cancelled = false;

    const fetchFirmwareUpdatePreviews = async () => {
      if (devices.length === 0) {
        setFirmwareUpdatePreviews({});
        return;
      }

      const result = await backend.create(
        "devices/firmware-update/preview",
        { devices: devices.map((device) => device.deviceid) },
        null,
        false
      );

      if (cancelled || result.status !== backendStatus.SUCCESS) return;

      const previews = {};
      result.content.forEach((preview) => {
        previews[preview.deviceid] = preview;
      });
      setFirmwareUpdatePreviews(previews);
    };

    fetchFirmwareUpdatePreviews();

    return () => {
      cancelled = true;
    };
  }, [devices, backend]);

  const uieventsHandler = () => {
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      queryRef.current,
      sortRef.current
    );
  };

  useEffect(() => {
    header.notifications.set(true);

    uievents.setHandler("devices", uieventsHandler);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (sortQueryString === null) return;

    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );

    // eslint-disable-next-line
  }, [sortQueryString]);

  useEffect(() => {
    if (!user) return;

    if (!user.type) {
      setLoading(true);
      return;
    }

    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );

    // eslint-disable-next-line
  }, [user]);

  const selectDevice = (deviceid) => {
    devices.forEach((device) => {
      if (deviceid === device.deviceid) {
        handleProfileName(device);
        setSelectedDevice(device);
      }
    });
  };

  const updateSelectedProfile = async () => {
    let result = await backend.retrieveFresh(
      `devices/${selectedDevice.deviceid}/profile`
    );

    selectedDevice.profile = result.content;

    if (selectedDevice.profile.status === profileStatus.CONFIRMED) {
      addToast("Perfil associado com sucesso", {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });

      setSelectedDevice({ ...selectedDevice });
      return;
    }

    setTimeout(() => {
      updateSelectedProfile();
    }, 2000);
  };

  const onUnauthorized = () => {
    history.push("/login");
  };

  useEffect(() => {
    header.hidden.set(false);
    header.navigation.set(true);
    header.title.set("Dispositivos");

    fetchProfiles(backend, setProfiles, setLoading);

    setUsername(localStorage.getItem("username"));

    backend.setOnUnauthorized(onUnauthorized);

    // eslint-disable-next-line
  }, []);

  const getDevice = (deviceid) => {
    for (let device of devices) {
      if (device.deviceid === deviceid) {
        return device;
      }
    }
  };

  const submitDeviceUpdate = async (device) => {
    let oldDevice = getDevice(device.deviceid);

    if (deviceNeedsUpdate(oldDevice, device)) {
      await backend.update(`devices/${device.deviceid}`, device);
      setSelectedDevice({ ...device, profile: { ...device.profile } });
      addToast(`Salvo`, {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });
    }

    if (deviceProfileNeedsUpdate(oldDevice, device)) {
      let newDevice = { ...device, profile: { ...device.profile } };
      newDevice.profile = getProfileInfo(device.profile.profileid);
      newDevice.profile.status = profileStatus.ASSOCIATING;

      setSelectedDevice({ ...newDevice });
      await backend.create(
        `devices/${newDevice.deviceid}/profile`,
        newDevice.profile
      );

      setTimeout(() => {
        updateSelectedProfile();
      }, 2000);
    }

    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );
  };

  const sendAssociateRequest = async (data) => {
    let result = await backend.create("devices/associate_request", data);
    let response = result.content;

    if (response.success.length > 0) {
      addToast(
        response.success.length > 1
          ? "Foram associados " +
              response.success.length +
              " dispositivos ao seu perfil."
          : "Foi associado " +
              response.success.length +
              " dispositivo ao seu perfil.",
        {
          appearance: "success",
          autoDismiss: true,
          pauseOnHover: true,
        }
      );
    }

    response.error.forEach((device) => {
      addToast(device.deviceid + ": " + device.error, {
        appearance: "error",
        autoDismiss: false,
      });
    });
  };

  const findDeviceTimeout = async () => {
    setbuttonLoading(false);

    addToast("Nenhum dispositivo foi encontrado em sua rede.", {
      appearance: "error",
      autoDismiss: true,
      pauseOnHover: true,
    });
  };

  const findDevices = async () => {
    const { ipcRenderer } = window.require("electron");

    let data = {
      isp: localStorage.getItem("user_id"),
    };

    let findTimeout = setTimeout(findDeviceTimeout, FIND_TIMEOUT * 1000);

    setbuttonLoading(true);

    ipcRenderer.send("devices-request", data);

    ipcRenderer.once("devices-response", async (event, arg) => {
      clearTimeout(findTimeout);

      if (arg.devices.length > 0) {
        addToast(
          arg.devices.length > 1
            ? "Foram encontrados " +
                arg.devices.length +
                " dispositivos em sua rede."
            : "Foi encontrado " +
                arg.devices.length +
                " dispositivo em sua rede.",
          {
            appearance: "info",
            autoDismiss: true,
            pauseOnHover: true,
          }
        );
        await sendAssociateRequest({
          devices: arg.devices,
          password: arg.password,
        });
        fetchDevices(
          history,
          backend,
          setDevices,
          setLoading,
          pagerInfo,
          setPagerInfo,
          filterQueryString,
          sortQueryString
        );
      } else {
        addToast("Nenhum dispositivo foi encontrado em sua rede.", {
          appearance: "error",
          autoDismiss: true,
          pauseOnHover: true,
        });
      }

      setbuttonLoading(false);
    });
  };

  const setSort = (key, state) => {
    let sort = "";

    if (state !== ListOrderStates.NONE) {
      sort += `sort-${key}=${getOrderValue(state)}`;
    }

    setSortQueryString(sort);
  };

  const applyFilters = (searchQuery, status) => {
    const filters = buildDevicesFilterQuery(searchQuery, status);
    const newPagerInfo = { ...pagerInfo, pageNumber: 1 };

    setSearchFilterQueryString(searchQuery);
    setDeviceStatusFilter(status);
    setFilterQueryString(filters);
    setSearched(Boolean(searchQuery || status));
    setPagerInfo(newPagerInfo);
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      newPagerInfo,
      setPagerInfo,
      filters,
      sortQueryString
    );
  };

  const refreshDevices = () => {
    if (refreshing) return;

    setRefreshing(true);
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    ).finally(() => setRefreshing(false));
  };

  const getOrderValue = (state) => {
    switch (state) {
      case ListOrderStates.UP:
        return 1;
      case ListOrderStates.DOWN:
        return -1;
      default:
        return 0;
    }
  };

  // Keep the total width stable in both layouts. In particular, Status must
  // not absorb all the unused space when the InMesh column is visible.
  const columnSizes = (withMesh) =>
    withMesh
      ? {
          deviceid: "155px",
          model: "125px",
          clientid: "205px",
          profile: "210px",
          fw_version: "120px",
          mesh: "130px",
          assoc_time: "220px",
          status: "160px",
        }
      : {
          deviceid: "180px",
          model: "150px",
          clientid: "210px",
          profile: "250px",
          fw_version: "168px",
          assoc_time: "210px",
          status: "160px",
        };

  const getColumns = () => {
    const withMesh = showMeshColumn();
    const sizes = columnSizes(withMesh);

    let columns = [
      {
        header: "Dispositivo",
        size: sizes.deviceid,
        orderable: true,
        orderFn: (state) => setSort("deviceid", state),
      },
      // {header: 'Apelido', size: '150px', orderable: true, orderFn:(state) => setSort('alias', state)},
      {
        header: "Modelo",
        size: sizes.model,
        orderable: true,
        orderFn: (state) => setSort("model", state),
      },
      {
        header: "Cliente",
        size: sizes.clientid,
        orderable: true,
        orderFn: (state) => setSort("clientid", state),
      },
    ];

    if (username === "remotizeadm") columns.push({ header: "Provedor" });
    else
      columns.push({
        header: "Perfil",
        size: sizes.profile,
        orderable: true,
        orderFn: (state) => setSort("profile.name", state),
      });

    columns.push({
      header: "Versão",
      size: sizes.fw_version,
      align: "left",
      orderable: true,
      orderFn: (state) => setSort("fw_version", state),
    });

    if (withMesh) columns.push({ header: "InMesh", size: sizes.mesh });

    columns.push(
      {
        header: "Data Associação",
        size: sizes.assoc_time,
        orderable: true,
        orderFn: (state) => setSort("assoc_time", state),
      },
      {
        header: (
          <span className="devices-status-header">
            <span>Status</span>
            <button
              type="button"
              className={`devices-list-refresh ${refreshing ? "is-loading" : ""}`}
              title={refreshing ? "Atualizando lista" : "Recarregar dispositivos"}
              aria-label={refreshing ? "Atualizando lista" : "Recarregar dispositivos"}
              disabled={refreshing}
              onClick={(event) => {
                event.stopPropagation();
                refreshDevices();
              }}
            >
              <span className="devices-list-refresh-icon" aria-hidden="true">
                ↻
              </span>
            </button>
          </span>
        ),
        size: sizes.status,
        orderable: true,
        orderFn: (state) => setSort("status", state),
      }
    );

    return columns;
  };

  const getProfileInfo = (id) => {
    for (let profile of profiles) {
      if (profile.id === id)
        return {
          profileid: profile.id,
          name: profile.name,
        };
    }
  };

  const copyMeshMac = async (event, mac) => {
    // The whole row opens the device details on click; keep that from firing.
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(mac);
      addToast(`MAC ${mac} copiado`, {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });
    } catch (error) {
      addToast("Não foi possível copiar o MAC", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 2500,
      });
    }
  };

  const meshSecondaries = (mesh) =>
    mesh && mesh.role === meshRole.PRIMARY && Array.isArray(mesh.neighbors)
      ? mesh.neighbors.filter((neighbor) => neighbor && neighbor.mac)
      : [];

  const getMeshRole = (device) => {
    const mesh = meshRoles[device.deviceid];

    if (!mesh || !meshRoleLabels[mesh.role])
      return <span className="device-mesh-role unknown">—</span>;

    const updated = mesh.updated_at
      ? `Atualizado em ${getDateString(mesh.updated_at)}`
      : "";

    const secondaries = meshSecondaries(mesh);

    const label =
      secondaries.length > 0
        ? `${meshRoleLabels[mesh.role]} +${secondaries.length}`
        : meshRoleLabels[mesh.role];

    if (secondaries.length === 0)
      return (
        <span className={`device-mesh-role ${mesh.role}`} title={updated}>
          {label}
        </span>
      );

    // Primary with secondaries: hovering or focusing opens the MAC popover.
    return (
      <span
        className="device-mesh-role-wrap"
        tabIndex={0}
        onMouseEnter={(event) => openMeshPopover(event, device.deviceid)}
        onMouseLeave={closeMeshPopover}
        onFocus={(event) => openMeshPopover(event, device.deviceid)}
        onBlur={closeMeshPopover}
      >
        <span className={`device-mesh-role ${mesh.role}`} title={updated}>
          {label}
        </span>
      </span>
    );
  };

  const renderMeshPopover = () => {
    if (!meshPopover) return null;

    const secondaries = meshSecondaries(meshRoles[meshPopover.deviceid]);
    if (secondaries.length === 0) return null;

    return createPortal(
      <div
        className="device-mesh-neighbors"
        style={{ top: meshPopover.top, left: meshPopover.left }}
        onMouseEnter={keepMeshPopover}
        onMouseLeave={closeMeshPopover}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="device-mesh-neighbors-title">
          {secondaries.length === 1
            ? "1 secundário — clique para copiar o MAC"
            : `${secondaries.length} secundários — clique para copiar o MAC`}
        </div>
        {secondaries.map((neighbor) => (
          <button
            type="button"
            key={neighbor.mac}
            className="device-mesh-neighbor"
            title={[neighbor.name, neighbor.model]
              .filter((value) => !!value)
              .join(" · ")}
            // Keep the focus on the badge so the popover survives the click.
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => copyMeshMac(event, neighbor.mac)}
          >
            <span
              className={`device-status-icon ${
                neighbor.is_online ? "online" : "offline"
              }`}
            ></span>
            <span className="device-mesh-neighbor-mac">{neighbor.mac}</span>
            {neighbor.name ? (
              <span className="device-mesh-neighbor-name">{neighbor.name}</span>
            ) : null}
          </button>
        ))}
      </div>,
      document.body
    );
  };

  const getDeviceStatus = (device) => {
    const {
      deviceid,
      status: deviceCurrentStatus,
      assoc_status,
      fw_version,
      model,
    } = device;
    let status = deviceCurrentStatus;
    if (!assoc_status) status = deviceStatus.ERROR;

    switch (status) {
      case deviceStatus.CONFIRMING:
        return (
          <span className="device-status">
            <Loading show={true} mini></Loading>
          </span>
        );
      case deviceStatus.WAITING:
        return (
          <span className="device-status">
            <div className="device-status-icon waiting"></div> Associando{" "}
          </span>
        );
      case deviceStatus.OFFLINE:
        return (
          <span className="device-status">
            <div className="device-status-icon offline"></div> Offline
          </span>
        );
      case deviceStatus.ONLINE:
        return (
          <span className="device-status">
            <div className="device-status-icon online"></div>
            Online
            {canBeManaged({ model, fw_version, assoc_status }) &&
            !DeviceFactory.isGateway(model) ? (
              <div
                className="device-status-info"
                onClick={() => history.push(`/manage/${deviceid}/wan`)}
              >
                <SearchIcon size="18"></SearchIcon>
              </div>
            ) : (
              <InfoPopup message="Versão de firmware não gerenciável"></InfoPopup>
            )}
            {getFirmwareUpdateAction(device)}
            {/*{canBeManaged({model, fw_version, assoc_status}) && !DeviceFactory.isGateway(model)?
                        <div className='device-status-info' onClick={() => history.push(`/manage/${deviceid}/wan`)}>
                            <SearchIcon size='18'></SearchIcon>
                        </div>
                        :null}
                    {canBeManaged({model, fw_version, assoc_status}) && DeviceFactory.isGateway(model)?
                        <div className='device-status-info' onClick={() => history.push(`/manage/${deviceid}/gateway/info`)}>
                            <SearchIcon size='18'></SearchIcon>
                        </div>
                        :null}
                    {!canBeManaged({model, fw_version, assoc_status}) &&   
                    <InfoPopup message='Versão de firmware não gerenciável'></InfoPopup>} Liberar apenas quando página de gateway estiver pronta*/}
          </span>
        );
      case deviceStatus.UPDATING:
        return (
          <span className="device-status">
            <div className="device-status-icon updating"></div> Atualizando{" "}
          </span>
        );
      case deviceStatus.ERROR:
        return (
          <span className="device-status">
            <div className="device-status-icon error"></div> Não associado{" "}
          </span>
        );
      default:
        return "";
    }
  };

  const hasMoreThanOnePage = () => {
    return pagerInfo.totalElements / pagerInfo.pageSize > 1;
  };

  const changeDevicesPage = (pageNumber) => {
    let newPagerInfo = { ...pagerInfo, pageNumber: pageNumber };
    setPagerInfo(newPagerInfo);
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      newPagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );
  };

  const changeDevicesPageSize = (pageSize) => {
    let newPagerInfo = {
      ...pagerInfo,
      pageSize: pageSize,
      pageNumber: 1,
    };
    setPagerInfo(newPagerInfo);
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      newPagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );
  };

  const handleManualAssociationReturn = (deviceList) => {
    deviceList.forEach(({ device, status, error }) => {
      if (!status) {
        addToast(`${device}: ${error}`, {
          appearance: "error",
          autoDismiss: false,
        });
      }
    });
  };

  const submitAssociation = async (associate) => {
    let result = await backend.create("devices", associate);

    if (result.status === backendStatus.SUCCESS) {
      fetchDevices(
        history,
        backend,
        setDevices,
        setLoading,
        pagerInfo,
        setPagerInfo,
        filterQueryString,
        sortQueryString
      );
      handleManualAssociationReturn(result.content);
    }
  };

  const fillDevice = (device) => {
    device.model = device.model === "" ? "Confirmando" : device.model;
    device.fw_version =
      device.fw_version === "" ? "Confirmando" : device.fw_version;
    device.profile.name =
      device.profile.name === "" ? "Intelbras" : device.profile.name;
  };

  const deviceRemoved = () => {
    setInfoOpen(false);
    fetchDevices(
      history,
      backend,
      setDevices,
      setLoading,
      pagerInfo,
      setPagerInfo,
      filterQueryString,
      sortQueryString
    );
  };

  const getProfileName = (device) => {
    return (
      <span className="device-list-profile">
        {DeviceFactory.isTR069(device.model) ? (
          <span style={{ display: "flex", marginLeft: "5px" }}>
            -------
            <span style={{ marginLeft: "5px" }} className="info-popup">
              <TooltipText
                width={"350px"}
                message={"Não aplica perfil, utiliza customização de firmware"}
              />
              <HelpIcon size={13}></HelpIcon>
            </span>
          </span>
        ) : (
          device.profile.name
        )}

        {profileIsApplying(device) && <Loading show={true} mini></Loading>}
      </span>
    );
  };

  const getFirmwareUpdateAction = (device) => {
    const preview = firmwareUpdatePreviews[device.deviceid];
    if (!preview || !preview.available) return "";

    return (
      <div
        className="device-status-info device-status-update"
        title="Atualizar firmware"
        onClick={(event) => {
          event.stopPropagation();
          setFirmwareUpdate({
            ...preview,
            model: device.model,
            selectedSource: preview.options[0].source,
          });
        }}
      >
        <CircledArrow size="18"></CircledArrow>
      </div>
    );
  };

  const confirmFirmwareUpdate = async (confirmed) => {
    if (!confirmed || !firmwareUpdate || firmwareUpdateSaving) {
      if (!firmwareUpdateSaving) setFirmwareUpdate(null);
      return;
    }

    setFirmwareUpdateSaving(true);
    const result = await backend.create(
      `devices/${firmwareUpdate.deviceid}/firmware-update`,
      { source: firmwareUpdate.selectedSource },
      null,
      false
    );
    setFirmwareUpdateSaving(false);

    if (result.status !== backendStatus.SUCCESS) {
      addToast(
        result.content?.body?.error ||
          "Não foi possível iniciar a atualização do firmware.",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      setFirmwareUpdate(null);
      return;
    }

    setFirmwareUpdatePreviews((previews) => ({
      ...previews,
      [firmwareUpdate.deviceid]: {
        ...firmwareUpdate,
        available: false,
      },
    }));
    setFirmwareUpdate(null);
    addToast("Atualização de firmware iniciada.", {
      appearance: "success",
      autoDismiss: true,
    });
  };

  const getSelectedFirmwareOption = () => {
    if (!firmwareUpdate) return null;

    return firmwareUpdate.options.find(
      (option) => option.source === firmwareUpdate.selectedSource
    );
  };

  return (
    <div id="devices-page" className="page with-pager">
      {loading ? (
        <div className="loading"></div>
      ) : (
        <div className="container scroll-area">
          <div id="devices-page-toolbar">
            <div className="discovery">
              {isElectron() ? (
                <Button
                  title={"Procurar dispositivos em sua rede"}
                  id="btn-discovery"
                  text={"Procurar dispositivos"}
                  loading={buttonLoading ? 1 : 0}
                  onClick={findDevices}
                ></Button>
              ) : (
                <DeviceAsssociator
                  onSubmit={submitAssociation}
                  isNmsIntegrated={user?.isp?.is_nms_integrated}
                ></DeviceAsssociator>
              )}
            </div>

            <div className="devices-page-filters">
              <div className="device-status-filter">
                <label htmlFor="device-status-filter">Status</label>
                <select
                  id="device-status-filter"
                  value={deviceStatusFilter}
                  onChange={(event) =>
                    applyFilters(searchFilterQueryString, event.target.value)
                  }
                >
                  <option value="">Todos</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="updating">Atualizando</option>
                </select>
              </div>
              <MultiFieldSearch
                onSearch={(queryString) =>
                  applyFilters(queryString, deviceStatusFilter)
                }
                onClear={() => applyFilters("", "")}
              ></MultiFieldSearch>
            </div>
          </div>

          {pagerInfo.hasGateways ? (
            <Tabs
              route={history.location.pathname}
              activateFn={(route) => history.push(route)}
              data={[
                { label: "Roteadores", route: "0" },
                { label: "Gateways", route: "1" },
              ]}
            ></Tabs>
          ) : null}

          {devices.length === 0 || (
            <div className="devices-list-container">
              <List
                breakWidth={900}
                columns={getColumns()}
                lines={devices.map((device) => {
                  fillDevice(device);

                  let values = [
                    device.deviceid,
                    device.model,
                    device.clientid || "ND",
                  ];

                  if (username === "remotizeadm") values.push(device.isp);
                  else values.push(getProfileName(device));
                  values.push(
                    <span className="device-firmware-version">
                      {device.fw_version}
                    </span>
                  );

                  if (showMeshColumn()) values.push(getMeshRole(device));

                  values.push(
                    getDateString(device.assoc_time),
                    getDeviceStatus(device)
                  );

                  return values;
                })}
                onClick={(deviceLine) => {
                  selectDevice(deviceLine[0]);
                  setInfoOpen(true);
                }}
              ></List>
            </div>
          )}

          {renderMeshPopover()}

          {(hasMoreThanOnePage() || pagerInfo.pageSize !== 20) && (
            <div className="pager-container">
              <div className="devices-pager-controls">
                <button
                  type="button"
                  className="devices-pager-jump"
                  title="Primeira página"
                  disabled={pagerInfo.pageNumber <= 1}
                  onClick={() => changeDevicesPage(1)}
                >
                  «
                </button>
                <Pager
                  pageSize={pagerInfo.pageSize}
                  pageNumber={pagerInfo.pageNumber}
                  totalElements={pagerInfo.totalElements}
                  pageChangeFn={changeDevicesPage}
                ></Pager>
                <button
                  type="button"
                  className="devices-pager-jump"
                  title="Última página"
                  disabled={
                    pagerInfo.pageNumber >=
                    Math.ceil(pagerInfo.totalElements / pagerInfo.pageSize)
                  }
                  onClick={() =>
                    changeDevicesPage(
                      Math.ceil(pagerInfo.totalElements / pagerInfo.pageSize)
                    )
                  }
                >
                  »
                </button>
                <label className="devices-pager-size">
                  Exibir
                  <select
                    value={pagerInfo.pageSize}
                    onChange={(event) =>
                      changeDevicesPageSize(Number(event.currentTarget.value))
                    }
                  >
                    {[20, 40, 60, 80, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  por página
                </label>
              </div>
            </div>
          )}

          {!isElectron() && devices.length === 0 && !searched ? (
            <DownloadApps
              infoText='Você ainda não possui dispositivos associados à sua conta.
                    Para associar novos dispositivos manualmente, clique em "Associar dispositivos" acima.
                    Para associação automática baixe um de nossos aplicativos para desktop e instale em seu computador.'
            ></DownloadApps>
          ) : null}

          {isElectron() && devices.length === 0 ? (
            <DiagramRemotize
              infoText="Para associar dispositivos à sua conta você precisa conectar os equipamentos de rede conforme o
                    diagrama abaixo. Os roteadores deverão estar conectados via porta INTERNET a um switch com link DHCP e acesso à
                    internet, para que possam se comunicar com o servidor Remotize."
            ></DiagramRemotize>
          ) : null}

          {searched && devices.length === 0 && (
            <div>Nenhum dispositivo encontrado.</div>
          )}

          {infoOpen ? (
            <DeviceDetails
              id="device-details"
              device={{
                ...selectedDevice,
                profile: { ...selectedDevice.profile },
              }}
              setDevice={setSelectedDevice}
              onOverlayClick={() => {
                setInfoOpen(false);
              }}
              onProfileChanged={(e) => {
                selectedDevice.profile = getProfileInfo(e.target.value);
                setSelectedDevice({ ...selectedDevice });
              }}
              profiles={profiles}
              onSubmit={submitDeviceUpdate}
              onDeviceRemoved={deviceRemoved}
              isNmsIntegrated={user?.isp?.is_nms_integrated}
            ></DeviceDetails>
          ) : null}

          <Modal
            title="Atualizar firmware"
            show={firmwareUpdate !== null}
            content={
              firmwareUpdate ? (
                <div className="firmware-update-confirmation">
                  <p>
                    Dispositivo: <b>{firmwareUpdate.deviceid}</b>
                  </p>
                  <p>
                    Versão atual: <b>{firmwareUpdate.currentVersion}</b>
                  </p>
                  <p>
                    Nova versão: <b>{getSelectedFirmwareOption()?.version}</b>
                  </p>
                  {firmwareUpdate.options.length > 1 ? (
                    <div className="firmware-update-options">
                      <b>Firmware a aplicar</b>
                      {firmwareUpdate.options.map((option) => (
                        <label key={option.source}>
                          <input
                            type="radio"
                            name="firmware-source"
                            value={option.source}
                            checked={
                              firmwareUpdate.selectedSource === option.source
                            }
                            disabled={firmwareUpdateSaving}
                            onChange={() =>
                              setFirmwareUpdate({
                                ...firmwareUpdate,
                                selectedSource: option.source,
                              })
                            }
                          />
                          {option.source === "custom"
                            ? "Versão customizada"
                            : "Versão oficial"}{" "}
                          ({option.version})
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p>
                      Firmware selecionado: <b>{firmwareUpdate.options[0].source === "custom" ? "versão customizada" : "versão oficial"}</b>
                    </p>
                  )}
                  <p>
                    A atualização será iniciada imediatamente, sem aguardar a
                    janela de atualização. O dispositivo poderá reiniciar e
                    ficar indisponível durante o processo.
                  </p>
                </div>
              ) : null
            }
            onOverlayClick={() => !firmwareUpdateSaving && setFirmwareUpdate(null)}
            onDismissClick={confirmFirmwareUpdate}
            type={ModalTypes.CONFIRM_WARNING}
            dismissText="Cancelar"
            confirmText={firmwareUpdateSaving ? "Iniciando..." : "Atualizar agora"}
          ></Modal>

          <DeviceTour />
        </div>
      )}
    </div>
  );
};

export default DevicesPage;
